/**
 * The region an EHR / SMART-on-FHIR launch named, persisted under its own
 * frozen key and BOUND TO THE AUTH IDENTITY THAT LAUNCHED IT.
 *
 * ## Why this exists as a durable carrier
 *
 * `getRegion()`'s fallback chain does not hold for an EHR launch.
 * `extractRegionFromAccessToken` reads `user_properties.region.v`, a
 * Kinde-specific claim a Heidi-issued EHR token does not carry, so it returns
 * `undefined` there; `kindeProperties.region` is likewise absent. The launch
 * itself takes the region from a `?region=` param or the five-minute
 * `COOKIE_REGION`, both of which are gone by the time the clinician reloads.
 * That left the persisted `auth.userRegion` slice field as the ONLY durable
 * carrier for those sessions — un-persisting it sends a non-AU EHR customer
 * who reloads to `Region.AU`, which selects the AU host for clinical coding
 * and dictation.
 *
 * So this atom carries what the slice field carried, for the launches that
 * actually depend on it. It is a same-value replacement, not a new input:
 * `getRegion()` and `useRegion()` read it in the slot IMMEDIATELY above
 * `userRegion`, and `userRegion` stays in both chains, so nothing between the
 * two slots moves and no case can reorder.
 *
 * `getOnInit: true` is load-bearing rather than decorative, for the same
 * reason it is on `widgetRegionAtom`: `getRegion()` is a plain function called
 * from non-React paths, often before anything has mounted. Without it the
 * first synchronous read after a cold start sees the fallback and the reload
 * falls through to AU — the exact failure this atom exists to prevent.
 *
 * ## Why the stored value carries a subject — the interesting part
 *
 * The field this replaced was SELF-CORRECTING, by two mechanisms at once.
 * `auth.userRegion` sat in the slot the next clinician's `setUserRegion`
 * overwrote, AND it was wiped by `store.dispatch.auth.clear()` — which is
 * reached from EIGHT non-test sites, because `authModel`'s `clear` returns a
 * fresh slice. This carrier sits ABOVE `userRegion`, so nothing downstream can
 * correct a stale value, and nothing writes it on login.
 *
 * A teardown on every one of those eight paths would fix today and leave the
 * ninth site someone adds next month — correctness resting on an unbounded,
 * permanent obligation. So the value is keyed to the identity instead: the
 * stored payload is `{ region, subject }`, where `subject` is the `sub` claim
 * of the token that owned the launch, and a DIFFERENT clinician structurally
 * cannot read it. That is the same argument `attachAuthRevisionTracking` makes
 * in `src/lib/auth/authRevisionTracking.ts` and for the same reason: an
 * observer of the auth state cannot miss a writer, whereas an enumeration of
 * the writers always can.
 *
 * ## `subject` is REQUIRED, and nothing is ever stamped retroactively
 *
 * A launch writes its region BEFORE any token exists — `useEpicLaunch` writes
 * from the query param and from `COOKIE_REGION` *above* its own
 * `if (code && profileName)` gate, so even an errored or abandoned launch
 * writes one — and the subject therefore cannot be supplied at write time.
 *
 * The first shape of this file persisted such a row with no subject and let the
 * observer stamp it with whatever identity turned up later. That was a hole, not
 * a gap in the binding: an abandoned launch left a subjectless row on disk, and
 * an unrelated clinician signing in days later CLAIMED it and then read its
 * region for good, because this slot sits above `userRegion`. That is the same
 * failure the identity binding exists to remove, surviving in the one state
 * where the row carried no identity — and a regression against main, where the
 * field sat *below* the next `setUserRegion` and self-corrected.
 *
 * So an unstamped row is never written at all. Until an identity is in force the
 * region is held in `pendingEhrLaunchRegionAtom`, which is MEMORY ONLY: no
 * storage, so nothing survives the page load that wrote it and a later session
 * has nothing to claim. `subject` on the stored payload is required, and the
 * validator rejects a row without one — which also makes any subjectless row
 * left on disk by an earlier build inert rather than claimable.
 *
 * Nothing needs the region to be persisted before the token exists: the
 * pre-token window is inside one page load, and a reload during it re-drives the
 * launch from the URL or the (five-minute) cookie anyway.
 *
 * `attachEhrLaunchRegionIdentityTracking` is the observer, wired once beside the
 * revision register in `src/store.ts`. On every change to the observed subject
 * it:
 *
 * - PERSISTS the pending region, stamped, when one is waiting. The pending
 *   region only exists in the memory of the page load that launched, so this is
 *   the launch claiming its own row rather than a later session claiming a
 *   stranger's.
 * - DROPS a stored row whose subject disagrees with the one in force. This is
 *   the cross-user case, and it needs no teardown to fire.
 * - DOES NOTHING when there is no subject at all. Deliberate, and the one rule
 *   here that must not be "tidied": boot-before-rehydrate has no token, and
 *   clearing there would destroy exactly the value the reload needs.
 *
 * Both readers go through `ehrLaunchRegionValueAtom`, which composes the pending
 * and stored halves, so `getRegion()` and `useRegion()` cannot end up applying
 * different rules to the same slot.
 *
 * The sign-out teardown (`clearEhrLaunchRegion`, called from
 * `clearLogoutStorage` and `clearPersistedModels`) is kept, but it is now
 * defence in depth rather than the thing correctness rests on: it shortens the
 * window in which a logged-out browser still holds a readable region. The
 * remaining window is pre-auth — no token, so no subject to judge by — and it
 * closes the instant the next clinician's token lands. That is why the other
 * five `auth.clear()` sites are deliberately NOT wired: adding them would buy a
 * slightly shorter window at the price of the obligation this design exists to
 * remove.
 *
 * ## Who writes it
 *
 * The three client-side EHR launch sites (`useEpicLaunch`, `useHaikuLaunch`,
 * `useGenericEhrLaunch`), each beside the `dispatch.auth.setUserRegion` it
 * already made — the dispatch stays, because in-session readers still go at
 * `state.auth.userRegion`. Deliberately NOT written by the Kinde/Auth0 region
 * writers (`useSetUserRegionFromToken`, onboarding): those regions are already
 * re-derivable from the profile and the token, and they only fire when the
 * token carries a region claim, so they would be an incomplete substitute for
 * the identity binding above rather than an addition to it.
 *
 * `create-ehr-launch-server-side-props.ts` also dispatches a region, inside
 * `getServerSideProps`, where there is no `localStorage` to write and its
 * dispatch against a module singleton is a known separate finding. Its region
 * reaches the browser through `COOKIE_REGION`, which `useEpicLaunch` reads and
 * writes here.
 */
import { extractIdpSubFromAccessToken } from '@heidi/auth/tokens';
import { logger } from '@heidi/runtime/logger';
import {
  defineVersionedStorage,
  type VersionedStorageEvent,
  type VersionedSyncStorage,
} from '@heidi/runtime/storage';
import { Region } from '@heidi/schema/ml-scribe';
import { atom, getDefaultStore } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

/**
 * FROZEN storage key — explicit, namespaced, never renamed.
 *
 * The declaration below is spelled as one line on purpose. Three gates read
 * it and they want different things: the ESLint rule takes a justified
 * directive, `cache-key-admission` takes the `UNCARVED DOMAIN KEY` marker with
 * a ticket, and `Migration Hygiene` refuses a ticket ID on any added line
 * EXCEPT inside a directive's `--` justification. Putting the marker in the
 * justification is the one spelling all three accept, and it is honest in each
 * reading: the directive really is justified, by that declaration.
 *
 * On the substance: `integrations` owns the launch, but it does not own the
 * precedence. The chain that reads this key (`src/helpers/getRegion.ts`)
 * composes a widget region, this EHR-launch region, the Kinde profile, the
 * invitation, the token and the hostname across every domain — app-shell by
 * construction, with no single domain's `/contracts` door to declare it in.
 */
// eslint-disable-next-line @heidi/rules/no-new-cache-key-declaration -- UNCARVED DOMAIN KEY (app-shell, ARD-1999): not a cache identity at all, but a browser-storage key registered in persistence-manifest.json and never read into a queryKey/mutationKey head
export const EHR_LAUNCH_REGION_STORAGE_KEY = 'heidi.ehrLaunch.region.v1';

const STORAGE_VERSION = 1;

/**
 * The stored payload. `subject` is REQUIRED — a row that cannot name the
 * identity it belongs to is not written, because such a row is claimable by
 * anyone. See the identity section above.
 */
export type StoredEhrLaunchRegion = {
  region: Region;
  subject: string;
};

function reportStorageEvent(event: VersionedStorageEvent): void {
  // Keys, versions and reasons only — never a value. A region is a market
  // identifier, but `subject` is a user identifier, so neither half is logged.
  if (event.type === 'fallback') {
    logger.warn('[ehrLaunchRegion] persisted region unreadable, using fallback', {
      key: event.key,
      reason: event.reason,
    });
    return;
  }
  if (event.type === 'write-failed') {
    logger.warn('[ehrLaunchRegion] persisted region could not be written', {
      key: event.key,
    });
    return;
  }
  logger.debug('[ehrLaunchRegion] persisted region event', event);
}

/**
 * A stored value that is not a well-formed `{ region }` falls back to
 * `undefined`, which continues the precedence chain at `userRegion` rather than
 * handing a junk value to the caller. This value selects a data-plane host, so
 * an unrecognised one must not be passed through verbatim the way the enclosing
 * chain passes through the values it trusts.
 *
 * This also rejects the two payloads earlier builds of this branch wrote: the
 * bare `Region`, and `{ region }` with no subject. The second is the one that
 * matters — it is the row an abandoned launch left behind, and rejecting it on
 * read is what makes it inert rather than claimable. No migration is owed for
 * either: `version` 1 never shipped outside the branch, and a rejected row reads
 * as "no launch recorded", which is the pre-carrier behaviour rather than a wrong
 * region.
 */
function isStoredEhrLaunchRegion(
  data: unknown
): data is StoredEhrLaunchRegion | undefined {
  if (data === undefined) return true;
  if (typeof data !== 'object' || data === null) return false;
  const candidate = data as { region?: unknown; subject?: unknown };
  if (!Object.values(Region).includes(candidate.region as Region)) return false;
  return typeof candidate.subject === 'string' && candidate.subject.length > 0;
}

const ehrLaunchRegionStorage = defineVersionedStorage<StoredEhrLaunchRegion | undefined>({
  key: EHR_LAUNCH_REGION_STORAGE_KEY,
  version: STORAGE_VERSION,
  fallback: undefined,
  validate: isStoredEhrLaunchRegion,
  onEvent: reportStorageEvent,
});

/**
 * `undefined` is a real value here and JSON has no representation for it:
 * `JSON.stringify({ v: 1, data: undefined })` drops `data` entirely, and the
 * decoder then reads every later load as corrupt and warns on each one.
 * Writing `undefined` removes the key instead, so the next read is a clean
 * absence — the same treatment `integrationAuthAtoms` gives its optional
 * fields.
 */
const ehrLaunchRegionAdapter: VersionedSyncStorage<StoredEhrLaunchRegion | undefined> = {
  ...ehrLaunchRegionStorage.adapter,
  setItem: (_key: string, value: StoredEhrLaunchRegion | undefined): void => {
    if (value === undefined) {
      ehrLaunchRegionStorage.clear();
      return;
    }
    ehrLaunchRegionStorage.write(value);
  },
};

/** The stored row, subject and all. Most readers want the value atom below. */
export const ehrLaunchRegionAtom = atomWithStorage<StoredEhrLaunchRegion | undefined>(
  EHR_LAUNCH_REGION_STORAGE_KEY,
  undefined,
  ehrLaunchRegionAdapter,
  { getOnInit: true }
);

/**
 * The region a launch has named but cannot yet attribute, because its token has
 * not arrived. MEMORY ONLY, deliberately — no storage adapter, so it dies with
 * the page load that wrote it and a later session has nothing to claim. This is
 * the whole of the fix for retroactive stamping.
 */
export const pendingEhrLaunchRegionAtom = atom<Region | undefined>(undefined);

/**
 * THE read door, for both halves of the precedence. `useRegion()` subscribes to
 * this and `getEhrLaunchRegion()` reads it off the default store, so the two
 * implementations of one precedence cannot apply different rules to this slot.
 *
 * The pending half wins when both are set: it is the launch happening now, and
 * the stored half can only be the same clinician's earlier one (a foreign row is
 * already dropped by the observer).
 */
export const ehrLaunchRegionValueAtom = atom<Region | undefined>(
  (get) => get(pendingEhrLaunchRegionAtom) ?? get(ehrLaunchRegionAtom)?.region
);

/**
 * The envelope behind the atom. Exposed for the same reason
 * `integrationAuthStorage` is: the repo's jest `jotai/utils` mock replaces
 * `atomWithStorage` with an implementation that ignores the storage argument,
 * so the versioned contract is only checkable here.
 */
export const ehrLaunchRegionStorageDoor = ehrLaunchRegionStorage;

/**
 * The `sub` of the token last observed in the auth slice.
 *
 * Module state rather than an atom because the observer is fed by a redux
 * subscription and this module must not import `@/store` — `src/store.ts`
 * imports THIS module to wire the observer, and the reverse edge would be a
 * cycle. `undefined` means "no identity in force", which is the permissive
 * case by design (see the identity section above).
 */
let observedSubject: string | undefined;

/**
 * Called by the launch sites beside their existing `setUserRegion` dispatch.
 *
 * Persists immediately when an identity is already in force — a relaunch inside
 * an authenticated session, or a generic SMART-on-FHIR launch whose token was
 * already in the slice. Otherwise the region is HELD IN MEMORY for the observer
 * to persist once this launch's own token lands; see the identity section above
 * for why an unattributable row is never written to disk.
 */
export function setEhrLaunchRegion(region: Region): void {
  const store = getDefaultStore();
  if (observedSubject === undefined) {
    store.set(pendingEhrLaunchRegionAtom, region);
    return;
  }
  store.set(pendingEhrLaunchRegionAtom, undefined);
  store.set(ehrLaunchRegionAtom, { region, subject: observedSubject });
}

/**
 * The region an EHR launch named. `getRegion()` is a plain function called
 * from non-React paths, so it reads through this getter; `useRegion()`
 * subscribes to `ehrLaunchRegionValueAtom` directly.
 */
export function getEhrLaunchRegion(): Region | undefined {
  return getDefaultStore().get(ehrLaunchRegionValueAtom);
}

/**
 * The sign-out teardown — defence in depth, not the correctness mechanism; the
 * identity binding is. It shortens the window in which a signed-out browser
 * still holds a readable region, and it is called from `clearLogoutStorage`
 * (covering `logout` and `logoutWithoutRedirect`) and from
 * `clearPersistedModels`.
 *
 * Writing the atom owns BOTH halves: the adapter above removes the row when the
 * value is `undefined`, so memory and storage cannot end up disagreeing.
 * Removing the row on its own would leave the live atom — which
 * `getEhrLaunchRegion()` reads, and which re-persists itself on the next write
 * — still holding the departing clinician's region.
 */
export function clearEhrLaunchRegion(): void {
  const store = getDefaultStore();
  // Both halves: a sign-out mid-launch would otherwise leave the pending region
  // readable for the rest of the page load.
  store.set(pendingEhrLaunchRegionAtom, undefined);
  store.set(ehrLaunchRegionAtom, undefined);
}

/** The store shape the observer needs, typed structurally to avoid `@/store`. */
type EhrLaunchRegionAuthStore = {
  getState: () => { auth?: { token?: string | undefined } | undefined };
  subscribe: (listener: () => void) => () => void;
};

/**
 * Reconcile the carrier against the identity now in force. See the identity
 * section at the top of this file for why each arm is what it is — in
 * particular why "no subject" does nothing at all, and why the pending half is
 * the ONLY thing a newly-arrived identity may claim.
 */
function reconcileIdentity(subject: string | undefined): void {
  if (subject === undefined) return;

  const store = getDefaultStore();

  // The launch that is happening now, claiming its own row. Nothing else can
  // reach this branch: the pending region is memory-only, so a session that did
  // not perform the launch has nothing waiting.
  const pending = store.get(pendingEhrLaunchRegionAtom);
  if (pending !== undefined) {
    store.set(pendingEhrLaunchRegionAtom, undefined);
    store.set(ehrLaunchRegionAtom, { region: pending, subject });
    return;
  }

  // A stored row is stamped by construction, so this is only ever the
  // cross-user case. There is deliberately no arm that stamps a stored row.
  const stored = store.get(ehrLaunchRegionAtom);
  if (stored && stored.subject !== subject) {
    store.set(ehrLaunchRegionAtom, undefined);
  }
}

/**
 * Wires the one observer that owns this carrier's validity: every store
 * notification where the token's `sub` differs from the previously observed one
 * stamps or drops the stored region, and nothing else does.
 *
 * Defined over the observed token rather than over the reducers that write it,
 * for the reason `attachAuthRevisionTracking` gives next door: redux-persist's
 * rehydrate merge writes the auth slice without any reducer of ours running, and
 * `dispatch.auth.clear()` is reached from eight non-test sites. An observer of
 * the state cannot miss either; an enumeration of the writers always can.
 *
 * What it may NOT do is claim a stored row for a newly-arrived identity. Only
 * the memory-only pending region is claimable, and only by the page load that
 * put it there.
 *
 * Returns the store's unsubscribe, so a caller that owns a throwaway store (a
 * test, a second store in a future shell) can detach it again.
 */
export function attachEhrLaunchRegionIdentityTracking(
  store: EhrLaunchRegionAuthStore
): () => void {
  const observe = (): void => {
    const token = store.getState().auth?.token;
    const subject = token ? extractIdpSubFromAccessToken(token) : undefined;
    if (subject === observedSubject) return;
    observedSubject = subject;
    reconcileIdentity(subject);
  };

  observe();
  return store.subscribe(observe);
}

/** Test-only: drops the observed identity so suites do not leak one another's. */
export function __resetObservedAuthSubjectForTests(): void {
  observedSubject = undefined;
}
